import React from 'react';
import { useTranslation } from 'react-i18next';
import { 
  GraduationCap, 
  Briefcase, 
  Heart, 
  Music, 
  Users, 
  Home, 
  Shield, 
  BookOpen, 
  MessageSquare, 
  Users2,
  ArrowRight,
  Phone,
  MapPin,
  CheckCircle,
  Star,
  Globe,
  Target,
  Lightbulb,
  HandHeart,
  Library,
  Archive,
  Wrench,
  Mic,
  Calendar,
  ChevronRight
} from 'lucide-react';
import { motion } from 'framer-motion';

interface ServiceCard {
  icon: React.ReactNode;
  title: string;
  description: string;
  items: string[];
}

interface Testimonial {
  quote: string;
  author: string;
  role: string;
  image: string;
}

const Services: React.FC = () => {
  const { t } = useTranslation();

  // Animation variants
  const fadeInUp = {
    hidden: { opacity: 0, y: 60 },
    visible: { opacity: 1, y: 0 }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const youthServices: ServiceCard[] = [
    {
      icon: <GraduationCap className="h-8 w-8 text-blue-600" />,
      title: "Vocational & Technical Training",
      description: "Empowering youth with practical skills for sustainable employment and economic independence.",
      items: [
        "Sewing, hairdressing, welding, mechanics",
        "Literacy and numeracy classes",
        "Special education for the deaf and mute",
        "Computer skills and digital literacy"
      ]
    },
    {
      icon: <Briefcase className="h-8 w-8 text-green-600" />,
      title: "Entrepreneurship & Economic Empowerment",
      description: "Building business acumen and financial independence through comprehensive training programs.",
      items: [
        "Business skills workshops",
        "Group Saving & Loan Associations (GSLA)",
        "E-commerce training & cooperative development",
        "Financial literacy and investment planning"
      ]
    },
    {
      icon: <Heart className="h-8 w-8 text-red-600" />,
      title: "Health Awareness & Peer Education",
      description: "Promoting health and well-being through peer-led initiatives and community outreach.",
      items: [
        "Reproductive health, HIV/AIDS, family planning",
        "Obesity and NCD prevention",
        "Trained peer educators and youth-friendly services",
        "Mental health support and counseling"
      ]
    },
    {
      icon: <Music className="h-8 w-8 text-purple-600" />,
      title: "Talent & Skills Development",
      description: "Nurturing creativity and cultural expression through arts, sports, and performance.",
      items: [
        "Sports, arts, music, dance, theater",
        "Cultural competitions and community showcases",
        "Traditional dance and music training",
        "Creative writing and storytelling"
      ]
    },
    {
      icon: <Users className="h-8 w-8 text-indigo-600" />,
      title: "Peace & Leadership Initiatives",
      description: "Building tomorrow's leaders and peacemakers through comprehensive leadership programs.",
      items: [
        "Public speaking, debate clubs",
        "Peace camps, cross-border dialogues",
        "Digital peacebuilding and human rights advocacy",
        "Community leadership and civic engagement"
      ]
    }
  ];

  const vulnerableServices: ServiceCard[] = [
    {
      icon: <Home className="h-8 w-8 text-orange-600" />,
      title: "Refugees & Disaster Victims",
      description: "Supporting those affected by displacement and crisis with comprehensive care services.",
      items: [
        "Psycho-social support and counseling",
        "Inclusion in peace and health programs",
        "Economic reintegration and skill building",
        "Educational support and language training"
      ]
    },
    {
      icon: <Shield className="h-8 w-8 text-pink-600" />,
      title: "People Living with HIV/AIDS",
      description: "Comprehensive support and care services for individuals and families affected by HIV/AIDS.",
      items: [
        "Counseling and group therapy",
        "Awareness campaigns and education",
        "Community-based support services",
        "Medical referrals and treatment support"
      ]
    },
    {
      icon: <Users2 className="h-8 w-8 text-gray-600" />,
      title: "Prisoners & Ex-Convicts",
      description: "Pathways to rehabilitation and reintegration for individuals seeking a fresh start.",
      items: [
        "Re-education and reintegration programs",
        "Skill-building and rehabilitation",
        "Social reinsertion follow-up",
        "Employment placement and support"
      ]
    },
    {
      icon: <MessageSquare className="h-8 w-8 text-teal-600" />,
      title: "Parents & Community Members",
      description: "Strengthening families and communities through education and support programs.",
      items: [
        "Parenting workshops and education",
        "Intergenerational dialogue sessions",
        "Community development initiatives",
        "Family counseling and mediation"
      ]
    }
  ];

  const publicServices = [
    {
      icon: <Library className="h-12 w-12 text-blue-600" />,
      title: "VJN Library",
      description: "Access a wide range of books, digital resources, and research tools for youth and the community.",
      link: "https://library.visionjeunessenouvelle.org.rw",
      features: ["Digital Resources", "Research Tools", "Community Access"]
    },
    {
      icon: <Archive className="h-12 w-12 text-green-600" />,
      title: "VJN Record",
      description: "Access VJN records, archives, and important documents for reference and research.",
      link: "https://record.visionjeunessenouvelle.org.rw",
      features: ["Historical Archives", "Document Access", "Research Support"]
    },
    {
      icon: <Wrench className="h-12 w-12 text-purple-600" />,
      title: "Vocational Training Center",
      description: "Vocational Training Center offering technical and professional courses for youth and adults.",
      link: "https://vtc.visionjeunessenouvelle.org.rw",
      features: ["Technical Courses", "Professional Training", "Certification Programs"]
    },
    {
      icon: <Mic className="h-12 w-12 text-red-600" />,
      title: "VJN African Stars Band",
      description: "Live band and orchestra for events, providing musical entertainment for all occasions.",
      link: "https://band.visionjeunessenouvelle.org.rw",
      features: ["Live Performances", "Event Entertainment", "Musical Training"]
    },
    {
      icon: <Music className="h-12 w-12 text-orange-600" />,
      title: "Iyizire Traditional Dance Ballet",
      description: "Traditional dance ballet for events and weddings, showcasing Rwandan culture and heritage.",
      link: "https://iyizire.visionjeunessenouvelle.org.rw",
      features: ["Cultural Performances", "Wedding Entertainment", "Traditional Arts"]
    },
    {
      icon: <Calendar className="h-12 w-12 text-indigo-600" />,
      title: "Events Halls",
      description: "Spacious and modern halls for weddings, conferences, and various events, accommodating different needs.",
      link: "https://events.visionjeunessenouvelle.org.rw",
      features: ["Wedding Venues", "Conference Facilities", "Event Planning"]
    }
  ];

  const testimonials: Testimonial[] = [
    {
      quote: "VJN's vocational training program gave me the skills and confidence to start my own tailoring business. Now I employ three other young people and contribute to my community's economic growth.",
      author: "Marie Uwimana",
      role: "Graduate, VJN Vocational Training",
      image: "/testimonials/marie.jpg"
    },
    {
      quote: "Through VJN's peacebuilding program, I learned how to resolve conflicts in my community and became a youth leader. The skills I gained help me every day in my work and personal life.",
      author: "Jean Baptiste",
      role: "Youth Leader, Peace Program",
      image: "/testimonials/jean.jpg"
    }
  ];

  const ServiceSection = ({ title, services, description }: { title: string; services: ServiceCard[]; description?: string }) => (
    <motion.div
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true }}
      variants={staggerContainer}
      className="mb-16"
    >
      <motion.div variants={fadeInUp} className="text-center mb-12">
        <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">{title}</h2>
        {description && (
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">{description}</p>
        )}
      </motion.div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {services.map((service, index) => (
          <motion.div
            key={index}
            variants={fadeInUp}
            className="bg-white rounded-2xl shadow-lg p-8 hover:shadow-xl transition-all duration-300 group border border-gray-100"
          >
            <div className="flex items-center mb-6">
              <div className="p-3 rounded-xl bg-gray-50 group-hover:bg-gray-100 transition-colors">
              {service.icon}
              </div>
              <h3 className="text-xl font-bold ml-4 text-gray-900">{service.title}</h3>
            </div>
            <p className="text-gray-600 mb-6 leading-relaxed">{service.description}</p>
            <ul className="space-y-3">
              {service.items.map((item, idx) => (
                <li key={idx} className="flex items-start text-gray-700">
                  <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 mr-3 flex-shrink-0" />
                  <span className="text-sm">{item}</span>
                </li>
              ))}
            </ul>
          </motion.div>
        ))}
      </div>
    </motion.div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <motion.section 
        initial="hidden"
        animate="visible"
        variants={staggerContainer}
        className="relative bg-gradient-to-br from-blue-600 via-blue-700 to-blue-800 text-white py-24 overflow-hidden"
      >
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="absolute top-0 right-0 w-96 h-96 bg-white/5 rounded-full -translate-y-48 translate-x-48"></div>
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-white/5 rounded-full translate-y-40 -translate-x-40"></div>
        
        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            variants={fadeInUp}
            className="text-center max-w-4xl mx-auto"
          >
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              Our Services
            </h1>
            <p className="text-xl md:text-2xl text-blue-100 mb-8 leading-relaxed">
              Building Lives, One Step at a Time
            </p>
            <p className="text-lg text-blue-200 max-w-3xl mx-auto leading-relaxed">
              From the playground to the peace table, from skill-building workshops to the silent suffering behind bars - Vision Jeunesse Nouvelle is there. Serving with hands that heal, minds that teach, and hearts that transform.
            </p>
          </motion.div>
        </div>
      </motion.section>

      {/* Introduction Section */}
      <motion.section 
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={staggerContainer}
        className="py-20 bg-white"
      >
        <div className="container mx-auto px-4">
          <motion.div
            variants={fadeInUp}
            className="max-w-4xl mx-auto text-center"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Making a Difference in Our Community
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed">
              At Vision Jeunesse Nouvelle, we serve youth and beyond - equipping individuals with tools, skills, and support to lead dignified, healthy, and purposeful lives. Whether through vocational training, health outreach, peacebuilding, or creative expression, our services reach communities across Rwanda and the region.
            </p>
          </motion.div>
        </div>
      </motion.section>

      {/* Public Services Section */}
      <motion.section 
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={staggerContainer}
        className="py-20 bg-gray-50"
      >
        <div className="container mx-auto px-4">
          <motion.div variants={fadeInUp} className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">VJN Public Services</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Discover our comprehensive range of public services designed to support and empower communities across Rwanda.
            </p>
          </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {publicServices.map((service, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                className="bg-white rounded-2xl shadow-lg p-8 hover:shadow-xl transition-all duration-300 group border border-gray-100"
              >
                <div className="text-center mb-6">
                  <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-2xl bg-gray-50 group-hover:bg-gray-100 transition-colors">
                    {service.icon}
          </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{service.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{service.description}</p>
          </div>
                
                <div className="mb-6">
                  <div className="flex flex-wrap gap-2">
                    {service.features.map((feature, idx) => (
                      <span key={idx} className="px-3 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded-full">
                        {feature}
                      </span>
                    ))}
          </div>
          </div>

                <a
                  href={service.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full bg-blue-600 text-white px-6 py-3 rounded-xl font-semibold hover:bg-blue-700 transition-colors flex items-center justify-center group"
                >
                  Visit Site
                  <ChevronRight className="h-4 w-4 ml-2 group-hover:translate-x-1 transition-transform" />
                </a>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* Main Services Sections */}
      <div className="container mx-auto px-4 py-20">
        <div className="max-w-7xl mx-auto">
          {/* Youth Services */}
          <ServiceSection 
            title="Services for Youth" 
            description="Comprehensive programs designed to empower young people with skills, knowledge, and opportunities for a brighter future."
            services={youthServices} 
          />

          {/* Vulnerable Populations Services */}
          <ServiceSection 
            title="Services for Vulnerable Populations" 
            description="Specialized support and care services for those who need it most, ensuring no one is left behind."
            services={vulnerableServices} 
          />

          {/* Testimonials Section */}
          <motion.section
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="py-20 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-3xl"
          >
            <motion.div variants={fadeInUp} className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Success Stories</h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                Hear from those whose lives have been transformed through our services and programs.
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-6xl mx-auto">
              {testimonials.map((testimonial, index) => (
                <motion.div
                  key={index}
                  variants={fadeInUp}
                  className="bg-white rounded-2xl shadow-lg p-8 hover:shadow-xl transition-all duration-300"
                >
                  <div className="flex items-center mb-6">
                    <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center text-white font-bold text-xl mr-4">
                      {testimonial.author.split(' ').map(n => n[0]).join('')}
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900">{testimonial.author}</h3>
                      <p className="text-gray-600">{testimonial.role}</p>
                    </div>
                  </div>
                  <blockquote className="text-gray-700 italic text-lg leading-relaxed">
                    "{testimonial.quote}"
                  </blockquote>
                  <div className="flex mt-4">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="h-5 w-5 text-yellow-400 fill-current" />
                    ))}
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.section>

          {/* CTA Section */}
          <motion.section
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="py-20"
          >
            <motion.div
              variants={fadeInUp}
              className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-3xl p-12 text-center text-white relative overflow-hidden"
            >
              <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -translate-y-32 translate-x-32"></div>
              <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/10 rounded-full translate-y-24 -translate-x-24"></div>
              
              <div className="relative z-10">
                <h2 className="text-3xl md:text-4xl font-bold mb-6">Ready to Get Started?</h2>
                <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
                  Join thousands of young people who have transformed their lives through our comprehensive services and programs.
                </p>
                <div className="flex flex-wrap justify-center gap-6">
              <a
                href="/contact"
                    className="bg-white text-blue-600 px-8 py-4 rounded-xl font-semibold hover:bg-gray-100 transition-colors flex items-center group"
              >
                    <Phone className="h-5 w-5 mr-2" />
                    Contact Us
                    <ChevronRight className="h-4 w-4 ml-2 group-hover:translate-x-1 transition-transform" />
              </a>
              <a
                href="/programs"
                    className="bg-blue-500 text-white px-8 py-4 rounded-xl font-semibold hover:bg-blue-400 transition-colors flex items-center group"
              >
                    View Programs
                    <ChevronRight className="h-4 w-4 ml-2 group-hover:translate-x-1 transition-transform" />
              </a>
              <a
                href="/centers"
                    className="bg-transparent border-2 border-white text-white px-8 py-4 rounded-xl font-semibold hover:bg-white hover:text-blue-600 transition-colors flex items-center group"
              >
                    <MapPin className="h-5 w-5 mr-2" />
                    Visit Centers
                    <ChevronRight className="h-4 w-4 ml-2 group-hover:translate-x-1 transition-transform" />
              </a>
                </div>
            </div>
          </motion.div>
          </motion.section>
        </div>
      </div>
    </div>
  );
};

export default Services;
